"use client";

import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Check, ChevronDown, Globe, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';

/**
 * Language Switcher Component
 * Allows users to switch between available languages
 */
export const LanguageSwitcher = ({ className = '', variant = 'outline', size = 'sm' }) => {
  const {
    currentLanguage,
    availableLanguages,
    isLoadingTranslations,
    changeLanguage,
    getLanguageDisplayName,
    t
  } = useTranslation();

  const [open, setOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  const handleLanguageChange = async (language) => {
    if (language === currentLanguage || isChanging) return;
    
    setIsChanging(true);
    try {
      await changeLanguage(language);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsChanging(false);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={size}
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between min-w-[120px]", className)}
          disabled={isLoadingTranslations || isChanging}
        >
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            <span className="truncate">
              {getLanguageDisplayName(currentLanguage)}
            </span>
          </div>
          {(isLoadingTranslations || isChanging) ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ChevronDown className="h-4 w-4 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="end">
        <Command>
          <CommandEmpty>{t('common.noLanguagesFound', 'No languages found.')}</CommandEmpty>
          <CommandGroup>
            {availableLanguages.map((language) => (
              <CommandItem
                key={language}
                value={language}
                onSelect={() => handleLanguageChange(language)}
                className="cursor-pointer"
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    currentLanguage === language ? "opacity-100" : "opacity-0"
                  )}
                />
                <div className="flex flex-col">
                  <span className="font-medium">
                    {getLanguageDisplayName(language)}
                  </span>
                  <span className="text-xs text-muted-foreground uppercase">
                    {language}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default LanguageSwitcher;
